<template>
  <div>
    <el-radio-group ref="radios"
        v-model="pickerShortcutButton"
        @change="onPickerShortcutChange">
      <el-radio-button v-for="item in pickerShortcuts"
        :key="item.key"
        :label="item.text"
        :ref="item.key">
      </el-radio-button>
    </el-radio-group>
    <el-date-picker ref="picker"
      :style="'position: relative;top:2px;'"
      v-model="pickerValue"
      @change="onDatePickerChange"
      :start-placeholder="$t('cabinet.start_placeholder')"
      :end-placeholder="$t('cabinet.end_placeholder')"
      :range-separator="$t('cabinet.rangeSeparator')"
      :editable="false"
      :picker-options="pickerOptions"
      type="daterange"
      format="yyyy-MM-dd"
      align="right"
      unlink-panels>
    </el-date-picker>
  </div>
</template>

<script>
const ONE_HOUR = 3600 * 1000;

export default {
  name: 'DateRange',
  model: { // v-model 上绑定的值会返回 [startTime, endTime] 或 '15h' 两种形式的数据
    prop: 'pickerValue',
    event: 'change'
  },
  props: {
    maxRangeDays: { // 最长限制多少天之内（当天之前）
      type: [Number, String],
      default: Number.POSITIVE_INFINITY
    },
    shortcutDays: { // 左侧快捷方式
      types: String, // 9h,24h,3d,7d,14d
      default: ''
    },
    defaultValue: { // 默认值
      type: String, // 默认赋值 9h,24h,3d,7d,14d
      default: ''
    },
    delay: { // 如果有默认值，延迟的时间
      type: Number,
      default: 1000
    },
    isLinkageShortcuts: { // 是否联动快捷方式和picker，一般和 useWholeDayShortcuts 同时设置
      type: Boolean,
      default: false
    },
    useWholeDayShortcuts: { // 是否在快捷方式中使用完整天（00:00:00 ~ 23:59:59）
      type: Boolean,
      default: false
    }
  },
  data () {
    const me = this;

    return {
      pickerValue: '',
      pickerShortcutButton: '',
      pickerShortcuts: this.shortcutDays
        .replace(/\s/, '')
        .split(',')
        .map(shortcut => this._makePickerRange(shortcut)),
      pickerOptions: {
        disabledDate (time) {
          const t = time.getTime();
          const laterThenNow = t > Date.now();
          const earlierThenRange = t < Date.now() - ONE_HOUR * 24 * me.maxRangeDays;
          return earlierThenRange || laterThenNow;
        },
        onPick ({ maxDate, minDate }) { // !用 me 代替 this
          const bothSelected = !!maxDate && !!minDate;
          if (!bothSelected) return; // 解决首次不触发
          setTimeout(() => {
            if (!me.parsedPickerValue) {
              me.pickerValue = [minDate, maxDate];
              console.log('position 1', me.pickerValue);
            }
          }, 200);
        }
      },
      shortcutTimeout: null
    };
  },
  computed: {
    // 使用处理过的 pickerValue
    parsedPickerValue () {
      const pv = this.pickerValue;
      if (!pv) return '';
      if (!Array.isArray(pv)) return pv;
      if (!pv.filter(item => !!item).length) return '';
      const rst = pv.map(d => this._ensureDate(d, false).getTime());
      console.log(pv, '--->', rst, rst.map(d => (new Date(d)).toLocaleString()));
      return rst;
    }
  },
  mounted () {
    this.setDefaultShortcut();
  },
  watch: {
    defaultValue (newVal, oldVal) {
      this.setDefaultShortcut();
    },
    pickerShortcutButton (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.onPickerShortcutChange(newVal);
      }
    }
  },
  methods: {
    _ensureDate (date, zero = true) {
      if (!date) return new Date();
      if (/\d{13}/.test(date)) {
        date = new Date(date);
      }
      if (zero || this.useWholeDayShortcuts) {
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
      }
      return date;
    },
    _splitShortcut (shortcut) {
      const m = shortcut.match(/^(\d+)([d|h])$/i);
      if (!m) {
        throw new Error(`[DateRange] wrong param: ${shortcut}`);
      }
      const [_, num, unit] = m;
      return [num, unit.toLowerCase()];
    },
    _isLessThenOneDay (shortcut) {
      const m = this._splitShortcut(shortcut);
      const [num, unit] = m;
      return unit !== 'd' && num <= 24;
    },
    _makePickerRange (shortcut) {
      const m = this._splitShortcut(shortcut);
      if (!m) return null;

      const [num, unit] = m;
      const key = `cabinet.range${unit.toUpperCase()}`;

      const unitIsDay = unit === 'd';

      const ret = {
        key: `shortcut_${num}${unit}`,
        text: this.$t(key, [num]),
        range: shortcut,
        shortcut,
        lessThenOneDay: this._isLessThenOneDay(shortcut)
      };

      if (ret.lessThenOneDay) {
        const hNow = new Date();
        const hStart = new Date();
        hStart.setTime(hStart.getTime() - ONE_HOUR * num);
        ret.range = [hStart, hNow].map(d => d.getTime());
        return ret;
      }

      const days = unitIsDay
        ? num
        : Math.ceil(num / 24);
      const now = Date.now();
      let start = new Date(now);
      let today = new Date(now);
      if (this.useWholeDayShortcuts) {
        start = this._ensureDate(start, true);
        today = this._ensureDate(today, true);
        // 从 n - 1 天之前的 0 点
        start.setTime(start.getTime() - ONE_HOUR * 24 * (days - 1));
        // 到今天的 23:59:59
        today.setTime(today.getTime() + ONE_HOUR * 24 - 1000);
      } else {
        start.setTime(start.getTime() - ONE_HOUR * 24 * days);
      }
      ret.range = [start, today].map(d => d.getTime());

      return ret;
    },
    _triggerShortcut (item) {
      const { lessThenOneDay, range } = item; // 由 _makePickerRange() 形成的值

      // console.log(range, range.map(t => (new Date(t)).toLocaleString()));

      // 绕过 onDatePickerChange 直接触发 v-model
      if (lessThenOneDay || !this.isLinkageShortcuts) {
        this.$emit('change', range);
        console.log(777, lessThenOneDay, range, (new Date(range[0]).toLocaleString()));
        return;
      } 

      this.pickerValue = range;
      console.log('position 2', range);
    },
    _ignoreCallback (callbackName, handler) {
      const _origin = this[callbackName];
      this[callbackName] = () => {};
      handler();
      setTimeout(() => {
        this[callbackName] = _origin;
      }, 0);
    },
    onPickerShortcutChange (label) {
      clearTimeout(this._shortcutChgTimeout);
      console.log('~~shortcuts change~~', label, this.pickerShortcutButton);

      this._shortcutChgTimeout = setTimeout(() => {
        console.log('timeout after onPickerShortcutChange', label);

        const item = this.pickerShortcuts.filter(pitem => pitem.text === label);
        if (!item || !item.length) return;

        if (!this.isLinkageShortcuts 
          || this._isLessThenOneDay(item[0].shortcut)) {
          this._ignoreCallback('onDatePickerChange', () => {
            this.pickerValue = '';
          });
        } else {
          this.pickerValue = '';
        }
        console.log('position 3', typeof label, label);
        
        this._triggerShortcut(item[0]);
      }, label ? 200 : 0); // 防止重复触发后选不中的情况，让清空操作晚于可能的点击按钮
    },
    onDatePickerChange () {
      console.log('[DateRange] onDatePickerChange', this.pickerValue);

      // debounce 防止重复点击
      const now = Date.now();
      if (this._pickerChgDebounce && now - this._pickerChgDebounce < 100
        && this._pickerChgDbcValue === this.parsedPickerValue) {
        console.log('jump out');
        return;
      }
      this._pickerChgDebounce = now;
      this._pickerChgDbcValue = this.parsedPickerValue;

      if (!this.parsedPickerValue) {
        this.$emit('change', '');
        console.log('emit empty');
        return;
      }

      // eslint-disable-next-line prefer-const
      let [startTime, endTime] = this.parsedPickerValue;

      // 结束日的 23:59:59
      endTime = endTime + ONE_HOUR * 24 - 1000;
      if (endTime - startTime > ONE_HOUR * 24 * this.maxRangeDays) {
        this.$emit('error', 'overmax'); // <date-range @error="..." />
        this.pickerValue = '';
        console.log('position 4');
        return;
      }
      
      this._ignoreCallback('onPickerShortcutChange', () => {
        this.pickerShortcutButton = '';
        if (this.isLinkageShortcuts) {
          this.pickerShortcuts.slice().forEach((item) => {
            const [s, e] = item.range;
            if (s === startTime && e === endTime) {
              this.pickerShortcutButton = item.text;
            }
          });
        }
      });

      this.$emit('change', [startTime, endTime]);
      console.log('emit', startTime, endTime);
    },
    setDefaultShortcut () {
      const shortcut = this.defaultValue;
      if (!shortcut) return;

      const scItem = this._makePickerRange(shortcut);
      if (!scItem) return;

      const delay = Math.max(this.delay, 1000);
      setTimeout(() => {
        if (this.isLinkageShortcuts) {
          this._triggerShortcut(scItem);
          return;
        }
        setTimeout(() => {
          this.pickerShortcutButton = scItem.text;
        }, 100);
      }, delay);
    }
  } // end of methods
};
</script>



// WEBPACK FOOTER //
// src/components/common/DateRange.vue